import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDestinationSearch } from '../hooks/useDestinationSearch';
import type { DestinationSuggestion } from '../api/hotels.types';

interface DestinationAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  id: string;
  hasError?: boolean;
  errorId?: string;
  inputRef?: (node: HTMLInputElement | null) => void;
  onSelectDestination?: (destination: DestinationSuggestion) => void;
}

const INPUT_HEIGHT = 48;

function destinationLabel(dest: DestinationSuggestion): string {
  return `${dest.name}, ${dest.countryName ?? dest.countryCode}`;
}

// TODO(i18n): migrate visible copy to hotels.destinations.* once scope is confirmed.
const LOADING_TEXT = 'Buscando destinos\u2026';
const ERROR_TEXT = 'Error al buscar destinos';
const NO_RESULTS_TEXT = 'Sin resultados';

export function DestinationAutocomplete({
  value,
  onChange,
  id,
  hasError = false,
  errorId,
  inputRef,
  onSelectDestination,
}: DestinationAutocompleteProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputLocalRef = useRef<HTMLInputElement>(null);

  const termForApi = value.trim().length >= 3 ? value.trim() : '';

  const {
    suggestions: apiSuggestions,
    status: apiStatus,
    error: apiError,
  } = useDestinationSearch(termForApi);

  const suggestions: DestinationSuggestion[] = apiSuggestions;

  const isLoading = apiStatus === 'loading';
  const hasApiError = apiStatus === 'error';
  const showNoResults =
    apiStatus === 'idle' && value.trim().length >= 3 && apiSuggestions.length === 0 && !apiError;

  const closeList = useCallback(() => {
    setIsOpen(false);
    setActiveIndex(-1);
  }, []);

  const selectSuggestion = useCallback(
    (item: DestinationSuggestion) => {
      const label = destinationLabel(item);
      onChange(label);
      onSelectDestination?.(item);
      closeList();
    },
    [onChange, onSelectDestination, closeList],
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        closeList();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, closeList]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    onChange(nextValue);
    if (nextValue.trim().length > 0) {
      setIsOpen(true);
      setActiveIndex(-1);
    } else {
      closeList();
    }
  };

  const handleFocus = () => {
    if (value.trim().length > 0) {
      setIsOpen(true);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || suggestions.length === 0) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setIsOpen(true);
        setActiveIndex(0);
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActiveIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1));
        break;
      case 'Enter':
        event.preventDefault();
        if (activeIndex >= 0 && activeIndex < suggestions.length) {
          selectSuggestion(suggestions[activeIndex]);
        }
        break;
      case 'Escape':
        event.preventDefault();
        closeList();
        inputLocalRef.current?.focus();
        break;
      default:
        break;
    }
  };

  const inputProps = {
    'aria-activedescendant': isOpen && activeIndex >= 0 ? `${id}-option-${activeIndex}` : undefined,
    'aria-autocomplete': 'list' as const,
    'aria-controls': isOpen ? `${id}-listbox` : undefined,
    'aria-describedby': hasError && errorId ? errorId : undefined,
    'aria-expanded': isOpen ? ('true' as const) : ('false' as const),
    'aria-haspopup': 'listbox' as const,
    'aria-invalid': hasError ? ('true' as const) : ('false' as const),
    autoComplete: 'off' as const,
    role: 'combobox' as const,
  };

  const shouldShowDropdown =
    isOpen && (suggestions.length > 0 || isLoading || hasApiError || showNoResults);

  return (
    <div className='relative' ref={containerRef}>
      <input
        className='w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-gray-900 focus:outline-none'
        id={id}
        onBlur={() => {
          // Delay to allow click on suggestion
          window.setTimeout(closeList, 150);
        }}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        ref={(node) => {
          (inputLocalRef as React.MutableRefObject<HTMLInputElement | null>).current = node;
          inputRef?.(node);
        }}
        style={{ height: INPUT_HEIGHT }}
        type='text'
        value={value}
        {...inputProps}
      />

      {shouldShowDropdown && (
        <ul
          className='absolute left-0 right-0 z-50 mt-1 max-h-60 overflow-auto rounded border border-gray-200 bg-white shadow-lg'
          id={`${id}-listbox`}
          role='listbox'
          style={{ top: '100%' }}
        >
          {isLoading && (
            <li aria-live='polite' className='px-3 py-2 text-sm text-gray-400 italic' role='status'>
              {LOADING_TEXT}
            </li>
          )}

          {hasApiError && (
            <li aria-live='assertive' className='px-3 py-2 text-sm text-red-500' role='alert'>
              {ERROR_TEXT}
            </li>
          )}

          {showNoResults && <li className='px-3 py-2 text-sm text-gray-400'>{NO_RESULTS_TEXT}</li>}

          {suggestions.map((item, index) => (
            <li
              aria-selected={index === activeIndex}
              className={`cursor-pointer px-3 py-2 text-sm ${
                index === activeIndex
                  ? 'bg-blue-100 text-blue-900'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
              id={`${id}-option-${index}`}
              key={item.id}
              onMouseDown={(event) => {
                event.preventDefault();
                selectSuggestion(item);
              }}
              role='option'
            >
              <span className='font-medium'>{item.name}</span>
              <span className='ml-1.5 text-xs text-gray-400'>
                {item.countryName ?? item.countryCode}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
